import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { type TContentBlockBase } from '@onesource/schemas';
import { getMediaUrl, useCompleteBlock } from '@onesource/services';
import { useEffect, useRef, useState } from 'react';
import { MdDownload, MdPictureAsPdf } from 'react-icons/md';
import { Document, Page, pdfjs } from 'react-pdf';

import { downloadFileFromUrl } from '#/utils/download-file';
import { isIOS } from '#/utils/open-url';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set PDF.js worker for react-pdf v10+ (using CDN for compatibility with Vite)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfBlockViewerProps {
  block: TContentBlockBase;
}

/**
 * Renders a single PDF content block embedded on the lesson page, the document
 * equivalent of `VideoBlockPlayer`. Used by the INLINE_DOCUMENT lesson template
 * so the file is read in place instead of on a dedicated block page.
 *
 * Completion is gated behind an IntersectionObserver so the block is marked as
 * viewed only once it actually scrolls into the viewport — on a lesson page with
 * several blocks they must not all complete on load.
 */
export function PdfBlockViewer({ block }: PdfBlockViewerProps) {
  const theme = useTheme();

  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [pdfLoading, setPdfLoading] = useState<boolean>(true);
  const [useFallback, setUseFallback] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackedRef = useRef(false);
  const { mutate: completeBlock } = useCompleteBlock();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && !trackedRef.current) {
          trackedRef.current = true;
          completeBlock(block.id);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [block.id, completeBlock]);

  const fileUrl = getMediaUrl(block.pdfUrl);

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  // Download via blob so the CDN URL stays hidden and the file downloads instead
  // of opening in a new tab (iPad/Safari ignores the cross-origin download attr).
  const handleDownload = async () => {
    if (!fileUrl) {
      return;
    }
    setIsDownloading(true);
    try {
      await downloadFileFromUrl(fileUrl, block.pdfFilename || 'Document.pdf');
    } finally {
      setIsDownloading(false);
    }
  };

  // The react-pdf viewer fails on iOS Safari (pdfjs needs Promise.withResolvers,
  // missing on older iPads), so those users get a download-only experience.
  const isIosDevice = isIOS();

  return (
    <Box ref={containerRef} sx={{ pt: 2, pb: 2, px: { xs: 2, md: 3 }, background: 'white', borderRadius: '15px' }}>
      {/* Header bar — mirrors the inline video block header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: 9, md: 16 } }}>
          {block.title || block.pdfFilename || 'Document'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
          {block.pdfPageCount ? (
            <Typography variant="caption" sx={{ color: theme.palette.grey[600], whiteSpace: 'nowrap' }}>
              {block.pdfPageCount} pages
              {block.pdfFileSize ? ` • ${Math.round(block.pdfFileSize / 1024)} KB` : ''}
            </Typography>
          ) : null}
          <Button
            variant="text"
            size="small"
            disableRipple
            startIcon={
              isDownloading ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <MdDownload size={12} color={theme.palette.primary.main} />
              )
            }
            onClick={handleDownload}
            disabled={isDownloading || !fileUrl}
            sx={{ color: 'text.primary', fontSize: 9, minWidth: 'auto', '& .MuiButton-icon': { mr: 0.5 } }}
          >
            {isDownloading ? 'Downloading…' : 'Download'}
          </Button>
        </Box>
      </Box>

      {fileUrl ? (
        <>
          {/* Page / zoom controls — only for the react-pdf viewer (never on iOS) */}
          {!isIosDevice && !useFallback && !pdfLoading && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
                p: 1,
                backgroundColor: 'grey.100',
                borderRadius: 2,
              }}
            >
              <IconButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
                <NavigateBeforeIcon />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center' }}>
                Page {pageNumber} of {numPages || block.pdfPageCount || '?'}
              </Typography>
              <IconButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
                <NavigateNextIcon />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
                <ZoomOutIcon />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </Typography>
              <IconButton onClick={zoomIn} disabled={scale >= 2.5}>
                <ZoomInIcon />
              </IconButton>
            </Box>
          )}

          {isIosDevice ? (
            <Box
              sx={{
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'grey.100',
                borderRadius: '13px',
              }}
            >
              <MdPictureAsPdf size={48} color="#d32f2f" />
              <Typography variant="body2" color="text.secondary">
                Download the document to view it on this device.
              </Typography>
            </Box>
          ) : useFallback ? (
            // Fallback to an iframe for PDFs react-pdf can't load (e.g. CORS)
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                width: '100%',
                height: 750,
                borderRadius: '13px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <iframe
                src={`${fileUrl}#view=FitH`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={block.title || 'PDF Viewer'}
              />
            </Box>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '13px',
                overflow: 'auto',
                maxHeight: 750,
                minHeight: 400,
                width: '100%',
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
                backgroundColor: 'grey.200',
              }}
            >
              {pdfLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              <Document
                file={fileUrl}
                onLoadSuccess={({ numPages: loadedPages }) => {
                  setNumPages(loadedPages);
                  setPdfLoading(false);
                }}
                onLoadError={() => {
                  setPdfLoading(false);
                  setUseFallback(true);
                }}
                loading={null}
              >
                <Page pageNumber={pageNumber} scale={scale} renderTextLayer renderAnnotationLayer />
              </Document>
            </Box>
          )}

          {/* Mobile: the inline viewer is hidden, so offer the download instead */}
          {!isIosDevice && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                py: 3,
                bgcolor: 'grey.100',
                borderRadius: '13px',
              }}
            >
              <MdPictureAsPdf size={48} color="#d32f2f" />
              <Button
                variant="contained"
                size="small"
                startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <MdDownload />}
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading…' : 'Download'}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            height: 200,
            bgcolor: 'grey.100',
            borderRadius: '13px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <MdPictureAsPdf size={48} color="#999" />
          <Typography color="text.secondary">PDF not available</Typography>
        </Box>
      )}
    </Box>
  );
}
